/**
 * "Completion requirements" editor for PI-RADS / PI-QUAL project modules.
 *
 * Stores per-project overrides in `project_modules.config` via the
 * `PATCH /projects/:projectId/modules/:moduleId` endpoint.
 */

import { Button } from "@frontend/components/ui/button"
import { Checkbox } from "@frontend/components/ui/checkbox"
import { Label } from "@frontend/components/ui/label"
import { Switch } from "@frontend/components/ui/switch"
import {
  PIQUAL_DEFAULT_REQUIRED_FIELDS,
  PIQUAL_FIELD_LABELS,
  PIQUAL_REQUIRABLE_FIELDS,
  type PiqualRequirableField,
  resolvePiqualConfig
} from "@shared/extensions/piqual/schema"
import {
  PIRADS_DEFAULT_REQUIRED_FIELDS,
  PIRADS_FIELD_LABELS,
  PIRADS_REQUIRABLE_FIELDS,
  type PiradsRequirableField,
  resolvePiradsRequiredFields
} from "@shared/extensions/pirads/schema"
import { useMemo, useState } from "react"

import type { ProjectModuleItem } from "../../types"

interface ModuleCompletionRequirementsProps {
  module: ProjectModuleItem
  onSave: (payload: { config: Record<string, unknown> | null }) => void
  isSaving: boolean
}

export function ModuleCompletionRequirements({ module, onSave, isSaving }: ModuleCompletionRequirementsProps) {
  const type = module.template_type
  if (type !== "pirads" && type !== "piqual") return null

  return (
    <div className="mt-4 rounded-lg border p-3">
      <p className="text-sm font-semibold">Completion requirements</p>
      <p className="text-xs text-muted-foreground mt-1">
        Fields selected below must be answered on every row before this module can be marked complete.
      </p>
      {type === "pirads" ? (
        <PiradsRequirementsEditor
          key={module.config ?? "__default__"}
          module={module}
          onSave={onSave}
          isSaving={isSaving}
        />
      ) : (
        <PiqualRequirementsEditor
          key={module.config ?? "__default__"}
          module={module}
          onSave={onSave}
          isSaving={isSaving}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// PI-RADS
// ---------------------------------------------------------------------------

function PiradsRequirementsEditor({ module, onSave, isSaving }: ModuleCompletionRequirementsProps) {
  const initial = useMemo(
    () => new Set(resolvePiradsRequiredFields(module.config) as readonly PiradsRequirableField[]),
    [module.config]
  )
  const [selected, setSelected] = useState<Set<PiradsRequirableField>>(initial)

  const toggle = (field: PiradsRequirableField) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(field)) next.delete(field)
      else next.add(field)
      return next
    })
  }

  const handleSave = () => {
    // Round-trip an explicit required_fields list to avoid silent drift when defaults change.
    onSave({ config: { required_fields: Array.from(selected) } })
  }

  const handleResetDefaults = () => {
    setSelected(new Set(PIRADS_DEFAULT_REQUIRED_FIELDS))
    onSave({ config: null })
  }

  return (
    <div className="mt-3 space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {PIRADS_REQUIRABLE_FIELDS.map((field) => (
          <label key={field} className="flex items-center gap-2 text-xs cursor-pointer">
            <Checkbox checked={selected.has(field)} onCheckedChange={() => toggle(field)} />
            <span>{PIRADS_FIELD_LABELS[field]}</span>
          </label>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save requirements"}
        </Button>
        <Button size="sm" variant="ghost" onClick={handleResetDefaults} disabled={isSaving}>
          Reset to defaults
        </Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// PI-QUAL
// ---------------------------------------------------------------------------

function PiqualRequirementsEditor({ module, onSave, isSaving }: ModuleCompletionRequirementsProps) {
  const resolved = useMemo(() => resolvePiqualConfig(module.config), [module.config])
  const [selected, setSelected] = useState<Set<PiqualRequirableField>>(
    () => new Set(resolved.requiredFields as readonly PiqualRequirableField[])
  )
  const [allowEmpty, setAllowEmpty] = useState(resolved.allowEmpty)

  const toggle = (field: PiqualRequirableField) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(field)) next.delete(field)
      else next.add(field)
      return next
    })
  }

  const handleSave = () => {
    onSave({
      config: {
        required_fields: Array.from(selected),
        allow_empty: allowEmpty
      }
    })
  }

  const handleResetDefaults = () => {
    setSelected(new Set(PIQUAL_DEFAULT_REQUIRED_FIELDS))
    setAllowEmpty(false)
    onSave({ config: null })
  }

  return (
    <div className="mt-3 space-y-3">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        {PIQUAL_REQUIRABLE_FIELDS.map((field) => (
          <label key={field} className="flex items-center gap-2 text-xs cursor-pointer">
            <Checkbox checked={selected.has(field)} onCheckedChange={() => toggle(field)} />
            <span>{PIQUAL_FIELD_LABELS[field]}</span>
          </label>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={allowEmpty} onCheckedChange={(v) => setAllowEmpty(Boolean(v))} />
        <Label className="text-xs">Allow marking complete with zero answers</Label>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save requirements"}
        </Button>
        <Button size="sm" variant="ghost" onClick={handleResetDefaults} disabled={isSaving}>
          Reset to defaults
        </Button>
      </div>
    </div>
  )
}
